import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { BlFrontendButtonComponent } from '@app/core/wrappers/bl-frontend-button.component';
import {
  DialogData,
  DialogForm,
  DialogResult,
} from '@app/shared/components/form-dialog-base/form-dialog-base.component';
import { TranslocoModule } from '@jsverse/transloco';
import { InputTextModule } from 'primeng/inputtext';
import { map, Observable } from 'rxjs';
import { Country } from 'src/app/core/entity/locale';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableFormDialogBaseComponent } from 'src/app/core/i18n/translatable-form-dialog-base.component';
import { FirmSelectors } from 'src/app/core/state/firm/firm.selectors';
import { BlFrontendTextInputComponent } from 'src/app/core/wrappers/bl-frontend-text-input.component';

interface Result extends DialogResult {
  email: string;
  password: string;
}

interface Form extends DialogForm<unknown> {
  email: FormControl<string>;
  password: FormControl<string>;
}

interface Data extends DialogData {
  email: string | null;
  mode: 'ACTIVATE' | 'UPDATE';
}

@Component({
  template: `
    <ng-container *transloco="let t; prefix: scope">
      <form [formGroup]="form" (ngSubmit)="onSubmit()" pFocusTrap>
        <p [innerHTML]="t('enterIntegrationText')" class="mb-6"></p>

        <bl-frontend-text-input formControlName="email" [text]="t('apiEmail')"></bl-frontend-text-input>

        <bl-frontend-text-input
          type="password"
          formControlName="password"
          [text]="t('apiPassword')"></bl-frontend-text-input>

        <div class="bg-bl-grey-100 rounded-lg mt-6 p-4" *ngIf="isActivateMode">
          <p class="mb-0">{{ t('notCustomerQuestion') }}</p>
          <div [innerHTML]="readMoreLink$ | async"></div>
        </div>

        <div class="flex flex-row w-full justify-end mt-8" *transloco="let t2; prefix: 'shared'">
          <bl-frontend-button
            type="button"
            variant="secondary"
            (onclick)="close(null)"
            [text]="t2('cancel')"></bl-frontend-button>
          <bl-frontend-button
            type="submit"
            variant="primary"
            class="ml-2"
            [disabled]="(formValidity$ | async) !== true"
            [text]="t('activateIntegration')"></bl-frontend-button>
        </div>
      </form>
    </ng-container>
  `,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    InputTextModule,
    ReactiveFormsModule,
    BlFrontendButtonComponent,
    TranslocoModule,
    BlFrontendTextInputComponent,
  ],
  providers: [getTranslationProvider('ActivateIntegrationDialogComponent')],
})
export class ActivateIntegrationDialogComponent extends TranslatableFormDialogBaseComponent<Data, Result, Form> {
  readMoreLink$: Observable<string>;
  formValidity$: Observable<boolean>;

  get isActivateMode(): boolean {
    return this.config.data.mode === 'ACTIVATE';
  }

  private readMoreLinkKeys = {
    [Country.SE]: 'readMoreAboutDueLinkSE',
    [Country.NL]: 'readMoreAboutDueLinkNL',
  };

  protected override onInitialized = () => {
    this.form = this.builder.group({
      email: new FormControl(this.config.data.email, [Validators.required]),
      password: new FormControl(null, [Validators.required]),
    });

    this.formValidity$ = this.form.statusChanges.pipe(map((status) => status === 'VALID'));

    this.readMoreLink$ = this.store
      .select(FirmSelectors.selectCountryCode)
      .pipe(map((countryCode) => this.translate(this.readMoreLinkKeys[countryCode] || '')));
  };

  onSubmit() {
    const { email, password } = this.form.value;
    this.close({ email, password });
  }
}
